import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getStory, Story } from '../api/stories'
import { Calendar, User, ArrowLeft } from 'lucide-react'
import { resolveImageUrl, getStaticImageUrl } from '../utils/imageUrl'

const StoryDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [story, setStory] = useState<Story | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStory = async () => {
      if (!id) {
        setError('Story ID not provided')
        setLoading(false)
        return
      }

      try {
        const data = await getStory(id)
        if (data) {
          setStory(data)
        } else {
          setError('Story not found')
        }
      } catch (error) {
        console.error('Error fetching story:', error)
        setError('Failed to load story')
      } finally {
        setLoading(false)
      }
    }

    fetchStory()
  }, [id])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const toApiImageUrlIfPossible = (src: string) => resolveImageUrl(src)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading story...</p>
        </div>
      </div>
    )
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Story Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The story you\'re looking for doesn\'t exist.'}</p>
          <Link
            to="/blog"
            className="btn-primary inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stories
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stories
          </Link>
        </div>
      </div>

      {/* Story Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Story Image */}
          {story.imageUrl && (
            <div className="aspect-video w-full overflow-hidden">
              <img 
                src={toApiImageUrlIfPossible(story.imageUrl)} 
                alt={story.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const staticUrl = story.imageUrl ? getStaticImageUrl(story.imageUrl) : ''
                  if (staticUrl && e.currentTarget.src !== staticUrl) {
                    e.currentTarget.src = staticUrl
                    return
                  }
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
                }}
              />
            </div>
          )}

          {/* Story Header */}
          <div className="p-8">
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(story.createdAt)}
              <span className="mx-2">•</span>
              <User className="h-4 w-4 mr-1" />
              {story.authorName}
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-6">{story.title}</h1>

            {/* Tags */}
            {story.tags && story.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {story.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Story Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                {story.content}
              </div>
            </div>

            {/* Story Views */}
            <div className="flex items-center justify-end mt-8 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {story.viewCount} views
              </div>
            </div>
          </div>
        </article>

        {/* Related Stories or Call to Action */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Share Your Story</h3>
          <p className="text-gray-600 mb-6">
            Have a story to share about your experience with HarvestHelp? We'd love to hear it!
          </p>
          <Link
            to="/stories/create"
            className="btn-primary inline-flex items-center"
          >
            Write Your Story
          </Link>
        </div>
      </div>
    </div>
  )
}

export default StoryDetailPage
